/**
 * Load the Access JWT from the Pages origin so cross-origin API calls can
 * send Cf-Access-Jwt-Assertion (cookie is HttpOnly and host-scoped).
 */

const ACCESS_JWT_PATH = "/access-jwt";

let cached: string | null | undefined;

export function resetAccessJwtCache(): void {
  cached = undefined;
}

export async function getAccessJwtAssertion(
  fetchImpl: typeof fetch = fetch,
): Promise<string | null> {
  if (cached !== undefined) return cached;
  try {
    const response = await fetchImpl(ACCESS_JWT_PATH, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) {
      cached = null;
      return null;
    }
    const data: unknown = await response.json();
    const token =
      data &&
      typeof data === "object" &&
      "token" in data &&
      typeof data.token === "string"
        ? data.token.trim()
        : "";
    cached = token.length > 0 ? token : null;
    return cached;
  } catch {
    cached = null;
    return null;
  }
}

function defaultGetAccessJwt(): Promise<string | null> {
  if (import.meta.env.MODE === "test") return Promise.resolve(null);
  return getAccessJwtAssertion();
}

export async function applyAccessJwtHeader(
  headers: Headers,
  options: {
    accessJwt?: string | null;
    getAccessJwt?: () => Promise<string | null>;
  } = {},
): Promise<void> {
  if (headers.has("Cf-Access-Jwt-Assertion")) return;
  const token =
    options.accessJwt !== undefined
      ? options.accessJwt
      : await (options.getAccessJwt ?? defaultGetAccessJwt)();
  if (token) headers.set("Cf-Access-Jwt-Assertion", token);
}
